import { isLocalDev } from '../env';

import type { SupportedCountry } from '../types/supported-countries';

/**
 * This file is used to target services or content based on the country where you applied for an insurance, not the translations of a country
 */
type LocalePerCountryKey<Key extends string, PartialKey extends string> = Record<
  SupportedCountry,
  Record<Key, string> & Partial<Record<PartialKey, string>>
>;

type SocialChannel = 'facebook' | 'linkedin' | 'twitter' | 'instagram';
type LandingPages = 'report_damage' | 'contact';
type ContactKeys = 'contact_email' | 'contact_copyright' | 'support_email' | 'contact_phone';
type OptionalContactKeys = 'contact_whatsapp';
type Privacy = 'privacy_statement_link';

export const content: LocalePerCountryKey<
  `social_${SocialChannel}_link` | `landing_page_${LandingPages}` | ContactKeys | Privacy,
  OptionalContactKeys
> = {
  nl: {
    social_facebook_link: 'https://www.facebook.com/insifyverzekeringen',
    social_linkedin_link: 'https://www.linkedin.com/company/insify/',
    social_instagram_link: 'https://www.instagram.com/insify.nl/',
    social_twitter_link: 'https://twitter.com/insify',
    landing_page_report_damage: 'https://insify.nl/schade/',
    landing_page_contact: 'https://insify.nl/contact/',
    contact_email: 'hello@insify.nl',
    support_email: 'support@insify.nl',
    contact_copyright: 'Insify NLD Agent B.V., Weesperplein 4B, 1018 XA Amsterdam, KvK 80084036',
    contact_phone: '020 888 1089',
    contact_whatsapp: 'https://wa.me/31208881089',
    privacy_statement_link: 'https://www.insify.nl/voorwaarden/privacybeleid/',
  },
  de: {
    social_facebook_link: 'https://www.facebook.com/insify.de',
    social_linkedin_link: 'https://www.linkedin.com/company/insify/',
    social_instagram_link: 'https://www.instagram.com/insify.de/',
    social_twitter_link: 'https://twitter.com/insify',
    landing_page_report_damage: 'https://insify.de/schaden/',
    landing_page_contact: 'https://insify.de/kontakt/',
    contact_email: 'hello@insify.de',
    support_email: 'support@insify.de',
    contact_copyright: 'Insify B.V., Platz der Einheit 2, 60327 Frankfurt am Main - Geschäftsstelle der Insify B.V.',
    contact_phone: '069 94323111',
    contact_whatsapp: 'https://wa.me/496994323111',
    privacy_statement_link: 'https://www.insify.de/datenschutz/',
  },
  fr: {
    social_facebook_link: 'https://www.facebook.com/insify.fr',
    social_linkedin_link: 'https://www.linkedin.com/company/insify/',
    social_instagram_link: 'https://www.instagram.com/insify.fr/',
    social_twitter_link: 'https://twitter.com/insify',
    landing_page_report_damage: 'https://www.insify.fr/declarer-un-sinistre/',
    landing_page_contact: 'https://www.insify.fr/contact/',
    contact_email: 'hello@insify.fr',
    support_email: 'support@insify.fr',
    contact_copyright: 'Insify B. V., 58 Avenue de la Grande Armée, 75017 Paris',
    contact_phone: '01 86 47 27 70',
    privacy_statement_link: 'https://www.insify.fr/politique-de-confidentialite/',
  },
};

// quick and dirty way to get the domain from the URL
export const getAppDomain = () => {
  if (isLocalDev) return 'nl';
  const host = window.location.host.split('.');
  const domain = host[host.length - 1];
  return domain;
};

export const useCountrySpecificContent = () => {
  return content[getAppDomain() as SupportedCountry];
};
